import { onSnapshot, Query, Firestore } from 'firebase/firestore';

import { reportAppError } from '@anyx/shared-utils';

import { NotificationRaw, NotificationWithReadState } from '../types';

import { listenToUserNotificationState } from './listenToUserNotificationState';

interface ListenerOptions {
  query: Query<NotificationRaw>;
  userId: string;
  onUpdate: (data: { notification: NotificationWithReadState; readState: boolean }) => void;
}

export function createLatestNotificationListener(
  firestore: Firestore,
  options: ListenerOptions
): { unsubscribe: () => void } {
  const { query, userId, onUpdate } = options;

  let currentNotification: NotificationRaw;
  let currentReadMap = false;
  let unsubscribeStates: () => void = () => undefined;

  const updateCombinedData = (
    currentNotification: NotificationRaw,
    currentReadMap: boolean,
    onUpdate: (data: { notification: NotificationWithReadState; readState: boolean }) => void
  ) => {
    const merged = {
      ...currentNotification,
      id: currentNotification.id,
      read: currentReadMap,
    };

    onUpdate({
      notification: merged,
      readState: currentReadMap,
    });
  };

  const unsubscribeNotifications = onSnapshot(
    query,
    (snapshot) => {
      try {
        const currentData = snapshot.docs[0];

        // Safeguard against empty or invalid snapshots
        if (!currentData || !currentData.id) {
          console.warn('[notifications] No valid notification found in the snapshot.');
          return;
        }

        // Create the notification object with a valid ID
        currentNotification = {
          ...currentData.data(),
          id: currentData.id,
          read: false,
        };

        // Set up the read state listener if the ID is valid
        unsubscribeStates = listenToUserNotificationState(
          firestore,
          userId,
          currentNotification.id,
          (isRead) => {
            currentReadMap = isRead;
            updateCombinedData(currentNotification, currentReadMap, onUpdate);
          }
        );

        // Update the combined data
        updateCombinedData(currentNotification, currentReadMap, onUpdate);
      } catch (error) {
        console.error('[notifications] Error processing snapshot:', error);
        reportAppError(error);
      }
    },
    // Error callback for the main listener
    (error) => {
      console.error('[notifications] Failed to subscribe to notifications:', error);
      reportAppError(error);
    }
  );

  return {
    unsubscribe: () => {
      unsubscribeNotifications();
      unsubscribeStates();
    },
  };
}
